import {
  BasicCommunitySummaryCard,
  BasicPlanSummaryCard,
  BasicSpecSummaryCard,
  PaidCommunitySummaryCard,
  PaidMasterPlannedCommunitySummaryCard,
  PaidPlanSummaryCard,
  PaidSpecSummaryCard,
} from 'features/summaryCard';
import MarkerType from '../enums/markerType';

/**
 * Returns the correct standardCard instance based on marker type and listing flags.
 *
 * Mapping logic:
 *   plan  + isBasic              → BasicPlanCard
 *   plan  + !isBasic             → PaidPlanCard
 *   spec  + isBasic              → BasicSpecCard
 *   spec  + !isBasic             → PaidSpecCard
 *   community + isMasterPlanned  → PaidMasterPlannedCommunityCard
 *   community + isBasic          → BasicCommunityCard
 *   community + !isBasic         → PaidCommunityCard
 *
 * @param {string}  type                     - Marker type string (MarkerType.*).
 * @param {boolean} isBasic                  - Whether the listing is a non-paid listing.
 * @param {boolean} isMasterPlannedCommunity - Whether the community is master-planned.
 * @returns {Object|null} A standardCard instance, or null for unrecognised types.
 */
export function getCardInstance(type, isBasic, isMasterPlannedCommunity) {
  switch (type) {
    case MarkerType.BuildablePlan.toString():
    case MarkerType.HousingDevelopment.toString():
      if (isMasterPlannedCommunity) {
        return new PaidMasterPlannedCommunitySummaryCard();
      }
      return isBasic ? new BasicCommunitySummaryCard() : new PaidCommunitySummaryCard();

    case MarkerType.PlanUnit.toString():
      return isBasic ? new BasicPlanSummaryCard() : new PaidPlanSummaryCard();

    case MarkerType.SpecUnit.toString():
      return isBasic ? new BasicSpecSummaryCard() : new PaidSpecSummaryCard();

    default:
      return null;
  }
}

/**
 * Viewport ratio thresholds for tooltip direction resolution.
 * A marker above yRatio=0.55 renders the card above itself; below that, below.
 * Horizontal thresholds push the card left or right when the marker is near an edge.
 */
const VERTICAL_UPPER_THRESHOLD    = 0.55;
const HORIZONTAL_LEFT_THRESHOLD   = 0.7;
const HORIZONTAL_RIGHT_THRESHOLD  = 0.3;
const CENTRAL_UPPER_BOUND         = 0.33;
const CENTRAL_LOWER_BOUND         = 0.66;

/**
 * Pure: resolves which quadrant of the map viewport the marker sits in.
 * Used to decide which direction the tooltip card should open toward.
 *
 * @param {Object} bounds   - Map bounds with { north, south, east, west }.
 * @param {Object} position - Object with `lat` and `lng` properties.
 * @returns {{ vertical: 'above'|'below'|'center', horizontal: 'left'|'right'|'center' }|null}
 */
export function resolveTooltipDirection(bounds, { lat, lng }) {
  if (lat == null || lng == null || !bounds) {
    return null;
  }

  const { north, south, east, west } = bounds;
  const xRatio = (lng - west) / (east - west);
  const yRatio = (north - lat) / (north - south);

  const getVerticalPosition = (y) => {
    if (y >= CENTRAL_UPPER_BOUND && y <= CENTRAL_LOWER_BOUND) { return 'center'; }
    if (y > VERTICAL_UPPER_THRESHOLD) { return 'above'; }
    return 'below';
  };

  const getHorizontalPosition = (x, isVerticalCenter) => {
    if (isVerticalCenter) { return x < 0.5 ? 'right' : 'left'; }
    if (x < HORIZONTAL_RIGHT_THRESHOLD) { return 'right'; }
    if (x > HORIZONTAL_LEFT_THRESHOLD) { return 'left'; }
    return 'center';
  };

  const vertical   = getVerticalPosition(yRatio);
  const horizontal = getHorizontalPosition(xRatio, vertical === 'center');

  return { vertical, horizontal };
}

/**
 * Pure: computes card pixel position relative to the map container.
 * All eight directional cases (above/below × left/right/center + pure left/right) are
 * handled explicitly so the caller doesn't need to know the positioning model.
 *
 * @param {DOMRect} markerRect
 * @param {DOMRect} containerRect
 * @param {{ vertical: string, horizontal: string }|null} direction
 * @returns {{ left: string, right: string, top: string, bottom: string, transform: string }}
 */
export function computeCardPosition(markerRect, containerRect, direction) {
  const markerLeft = markerRect.left - containerRect.left;
  const markerTop = markerRect.top - containerRect.top;
  const markerBottom = markerRect.bottom - containerRect.top;
  const markerRight = markerRect.right - containerRect.left;

  const markerCenterX = markerLeft + markerRect.width / 2;
  const markerCenterY = markerTop + markerRect.height / 2;

  const isAbove = direction?.vertical === 'above';
  const isBelow = direction?.vertical === 'below';

  const isLeft = direction?.horizontal === 'left';
  const isRight = direction?.horizontal === 'right';

  if (isAbove && isRight) {
    return {
      left: `${markerRight - 12}px`,
      right: 'auto',
      top: 'auto',
      bottom: `${containerRect.height - markerTop - 6}px`,
      transform: 'none',
    };
  }

  if (isAbove && isLeft) {
    return {
      left: 'auto',
      right: `${containerRect.width - markerLeft - 12}px`,
      top: 'auto',
      bottom: `${containerRect.height - markerTop - 6}px`,
      transform: 'none',
    };
  }

  if (isAbove) {
    return {
      left: `${markerCenterX}px`,
      right: 'auto',
      top: 'auto',
      bottom: `${containerRect.height - markerTop}px`,
      transform: 'translateX(-50%)',
    };
  }

  if (isBelow && isRight) {
    return {
      left: `${markerRight - 10}px`,
      right: 'auto',
      top: `${markerBottom - 6}px`,
      bottom: 'auto',
      transform: 'none',
    };
  }

  if (isBelow && isLeft) {
    return {
      left: 'auto',
      right: `${containerRect.width - markerLeft - 10}px`,
      top: `${markerBottom - 6}px`,
      bottom: 'auto',
      transform: 'none',
    };
  }

  if (isBelow) {
    return {
      left: `${markerCenterX}px`,
      right: 'auto',
      top: `${markerBottom}px`,
      bottom: 'auto',
      transform: 'translateX(-50%)',
    };
  }

  if (isLeft) {
    return {
      left: 'auto',
      right: `${containerRect.width - markerLeft - 2}px`,
      top: `${markerCenterY}px`,
      bottom: 'auto',
      transform: 'translateY(-50%)',
    };
  }

  if (isRight) {
    return {
      left: `${markerRight - 2}px`,
      right: 'auto',
      top: `${markerCenterY}px`,
      bottom: 'auto',
      transform: 'translateY(-50%)',
    };
  }

  // Default / center
  return {
    left: `${markerCenterX}px`,
    right: 'auto',
    top: `${markerBottom}px`,
    bottom: 'auto',
    transform: 'translateX(-50%)',
  };
}